import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';

interface UserProfileData {
  racerId: string;
  email: string;
  registrationName: string;
  fullName: string;
  motherName: string;
  birthPlace: string;
  birthDate: string;
  address: string;
  phoneNumber: string;
  isProfileComplete: boolean;
  notes?: string;
  height?: number;
  weight?: number;
}

const UserProfile: React.FC = () => {
  const [profile, setProfile] = useState<UserProfileData>({
    racerId: '',
    email: '',
    registrationName: '',
    fullName: '',
    motherName: '',
    birthPlace: '',
    birthDate: '',
    address: '',
    phoneNumber: '',
    isProfileComplete: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { currentUser, isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    fetchUserProfile();
  }, [currentUser, navigate]);

  const fetchUserProfile = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      const profileDoc = await getDoc(doc(db, 'userProfiles', currentUser.uid));

      if (profileDoc.exists()) {
        const profileData = profileDoc.data() as UserProfileData;
        setProfile(profileData);
      } else {
        // Ha még nincs profil, akkor az alapértelmezett adatokat állítjuk be
        setProfile({
          ...profile,
          racerId: `R${Math.floor(100000 + Math.random() * 900000)}`, // Egyedi vendég ID generálása
          email: currentUser.email || '',
          registrationName: currentUser.displayName || '',
          fullName: currentUser.displayName || ''
        });
      }
    } catch (error) {
      console.error('Hiba a profil lekérése során:', error);
      setError('Nem sikerült betölteni a profil adatokat. Kérjük, próbáld újra később.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser) return;

    try {
      setSaving(true);
      setError(null);

      // Ellenőrizzük, hogy minden kötelező mező ki van-e töltve
      const isComplete =
        profile.fullName.trim() !== '' &&
        profile.motherName.trim() !== '' &&
        profile.birthPlace.trim() !== '' &&
        profile.birthDate.trim() !== '' &&
        profile.address.trim() !== '' &&
        profile.phoneNumber.trim() !== '';

      const updatedProfile = {
        ...profile,
        isProfileComplete: isComplete,
        updatedAt: new Date(),
        height: profile.height || null,
        weight: profile.weight || null,
        notes: profile.notes || ''
      };

      // Mentjük a felhasználói profilba
      await setDoc(doc(db, 'userProfiles', currentUser.uid), updatedProfile);

      // Mentjük a racers kollekcióba is a vendég ID alapján
      if (profile.racerId) {
        await setDoc(doc(db, 'racers', profile.racerId), updatedProfile);
      }

      setProfile(prev => ({
        ...prev,
        isProfileComplete: isComplete
      }));

      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Hiba a profil mentése során:', error);
      setError('Nem sikerült menteni a profil adatokat. Kérjük, próbáld újra később.');
    } finally {
      setSaving(false);
    }
  };

  // Kilépés kezelése
  const handleLogout = async () => {
    if (window.confirm('Biztosan ki szeretnél jelentkezni?')) {
      try {
        await logout();
        navigate('/login');
      } catch (error) {
        console.error('Hiba a kijelentkezés során:', error);
      }
    }
  };

  // Mobil menü megnyitása/bezárása
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  if (!currentUser) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Navigation showPublicBooking={true} />

      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl font-semibold text-gray-900 mb-6">Profil adatok</h1>

          {saveSuccess && (
            <div className="mb-6 p-4 bg-green-100 text-green-800 rounded-md">
              A profil adatok sikeresen mentve!
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-100 text-red-800 rounded-md">
              {error}
            </div>
          )}

          <div className="bg-white overflow-hidden shadow-sm rounded-lg">
            <div className="p-6 bg-white border-b border-gray-200">
              {loading ? (
                <div className="text-center py-4">
                  <p className="text-gray-600">Profil adatok betöltése...</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Vendég ID
                      </label>
                      <input
                        type="text"
                        name="racerId"
                        value={profile.racerId}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Automatikusan generált azonosító
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email cím
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={profile.email}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        A fiókodhoz tartozó email cím
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Regisztrációs név
                      </label>
                      <input
                        type="text"
                        name="registrationName"
                        value={profile.registrationName}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        A fiókodhoz tartozó név
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Teljes név *
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={profile.fullName}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Anyja neve *
                      </label>
                      <input
                        type="text"
                        name="motherName"
                        value={profile.motherName}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Születési hely *
                      </label>
                      <input
                        type="text"
                        name="birthPlace"
                        value={profile.birthPlace}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Születési idő *
                      </label>
                      <input
                        type="date"
                        name="birthDate"
                        value={profile.birthDate}
                        onChange={handleInputChange}
                        max={new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Lakcím *
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={profile.address}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Telefonszám *
                      </label>
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={profile.phoneNumber}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="+36 30 123 4567"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Magasság (cm)
                      </label>
                      <input
                        type="number"
                        name="height"
                        value={profile.height || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="170"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Súly (kg)
                      </label>
                      <input
                        type="number"
                        name="weight"
                        value={profile.weight || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="70"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Megjegyzések
                    </label>
                    <textarea
                      name="notes"
                      value={profile.notes ?? ''}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      rows={4}
                      placeholder="További megjegyzések..."
                    />
                  </div>

                  <div className="mt-6">
                    <p className="text-sm text-gray-600 mb-4">
                      * A csillaggal jelölt mezők kitöltése kötelező a foglaláshoz.
                    </p>

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={saving}
                        className={`px-4 py-2 rounded-md ${saving
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                      >
                        {saving ? 'Mentés...' : 'Profil mentése'}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile; 